import { useFormContext } from 'react-hook-form';
import { useCallback } from 'react';
import { useAppTFunction } from '@retail/app/i18n';
import { PriceType } from '@retail/calculus/types';
import { ActivateAgreementSchema } from '@retail/purchase-agreements/components';
import { useConditionActions } from '@retail/calculus/context';
import { PurchaseAgreement } from '@retail/purchase-agreements/types';
import { PackageType } from '@retail/products/types';
import { getConditionBindingValuesFromLevelSchema } from '@retail/calculus/components';
import { ConditionsNew } from '@retail/calculus/types';
import { CreateConditionDialog } from '../../ConditionActionsBase';
import { CreatePurchaseConditionForm } from './CreatePurchaseConditionForm';

interface Props {
  open: boolean;
  assortmentId: number;
  agreements: PurchaseAgreement[];
  conditions?: ConditionsNew[];
  supplierNumber: number;
  onClose: () => void;
}

export function CreatePurchaseCondition({
  open,
  agreements,
  conditions,
  assortmentId,
  supplierNumber,
  onClose,
}: Props) {
  const t = useAppTFunction();

  const {
    handleSubmit,
    formState: { errors },
    reset,
  } = useFormContext<ActivateAgreementSchema>();

  const { createOrUpdateCondition, isLoadingPostChanges } = useConditionActions();

  const submitAgreements = useCallback(
    (levelSchema: ActivateAgreementSchema) => {
      createOrUpdateCondition({
        content: {
          purchase: {
            agreementId: Number(levelSchema.agreementId),
            priceType: levelSchema.priceType ? (levelSchema.priceType as PriceType) : undefined,
            packageType: levelSchema.packageType
              ? (levelSchema.packageType as PackageType)
              : undefined,
          },
        },
        bindingValue: getConditionBindingValuesFromLevelSchema({
          levelSchema,
          supplierNumber,
        }),
        validFrom: levelSchema.fromDate,
      }).then(() => {
        reset();
        onClose();
      });
    },
    [createOrUpdateCondition, supplierNumber, reset, onClose]
  );

  return (
    <CreateConditionDialog
      open={open}
      title={t('purchaseAgreements.agreement.activateAgreement')}
      supplierNumber={supplierNumber}
      isLoading={isLoadingPostChanges}
      isDisabled={Object.keys(errors).length > 0}
      dialogContent={
        <CreatePurchaseConditionForm
          assortmentId={assortmentId}
          agreements={agreements}
          conditions={conditions}
          supplierNumber={supplierNumber}
        />
      }
      onSubmit={handleSubmit(submitAgreements)}
      onClose={onClose}
    />
  );
}
